package com.dotdrives.web.shell

import com.dotdrives.web.ui.DotAvatar
import react.useState
import react.createContext
import react.use
import com.dotdrives.web.ui.useDialogFocus
import react.dom.aria.AriaRole
import react.dom.aria.AriaCurrent
import web.dom.ElementId
import react.dom.svg.ReactSVG.svg
import react.dom.svg.ReactSVG.path
import react.FC
import react.PropsWithChildren
import react.ReactNode
import react.dom.html.ReactHTML.aside
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.header
import react.dom.html.ReactHTML.main
import react.dom.html.ReactHTML.nav
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

enum class NavItem(
    val label: String,
    val icon: String,
) {
    Dashboard("Dashboard", "M3 10 12 3l9 7v11h-6v-7H9v7H3Z"),
    NextSteps("Next Steps", "m4 12 5 5L20 6"),
    Drives("Drives", "M4 4h16v16H4ZM4 9h16M9 9v11"),
    Givers("Givers", "M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8M2 21v-3a7 7 0 0 1 14 0v3M17 4a4 4 0 0 1 0 8M19 15a5 5 0 0 1 3 5v1"),
    Donations("Donations", "M12 21 3 12C-3 5 7-2 12 6 17-2 27 5 21 12Z"),
    Forms("Giving forms", "M5 3h14v18H5ZM8 7h8M8 12h8M8 17h5"),
    Updates("Updates", "M4 17 10 11l4 3 6-10M14 4h6v6"),
    Reports("Reports", "M4 20V10M10 20V4M16 20v-7M3 20h18"),
    Settings("Settings", "M4 6h16M4 12h16M4 18h16M8 3v6M16 9v6M10 15v6"),
}

external interface PortalShellProps : PropsWithChildren {
    var active: NavItem
    var onNavigate: (NavItem) -> Unit
    var userInitials: String
    var userName: String
    var orgName: String
    var onSwitchOrg: () -> Unit
    var onSignOut: () -> Unit
    var actions: ReactNode?
    var titleSuffix: String?
}

val OrganizationSwitchAvailable = createContext(false)

private external interface NavIconProps : react.Props { var item: NavItem }
private val NavIcon = FC<NavIconProps> { props ->
    svg {
        asDynamic().viewBox = "0 0 24 24"
        asDynamic().width = 20
        asDynamic().height = 20
        asDynamic().fill = "none"
        asDynamic().stroke = "currentColor"
        asDynamic().strokeWidth = 1.7
        asDynamic().strokeLinecap = "round"
        asDynamic().strokeLinejoin = "round"
        ariaHidden = true
        path { asDynamic().d = props.item.icon }
    }
}

val PortalShell = FC<PortalShellProps> { props ->
    val (moreOpen, setMoreOpen) = useState(false)
    val canSwitchOrg = use(OrganizationSwitchAvailable)
    val organizationSelection = use(OrganizationSelection)
    val activeOrg = organizationSelection?.organizations?.firstOrNull { it.id == organizationSelection.activeOrgId }
    val customerAccess = com.dotdrives.web.pages.admin.useCustomerAccess(activeOrg)
    val opener = react.useRef<org.w3c.dom.Element>(null)
    fun openMore() { opener.current = kotlinx.browser.document.activeElement; setMoreOpen(true) }
    useDialogFocus(moreOpen, "portal-more-sheet") { setMoreOpen(false) }
    react.useEffect(moreOpen) {
        if (moreOpen) {
            val oldOverflow = kotlinx.browser.document.body?.style?.getPropertyValue("overflow").orEmpty()
            kotlinx.browser.document.body?.style?.setProperty("overflow", "hidden")
            val resize: () -> Unit = { if (kotlinx.browser.window.innerWidth > 760) setMoreOpen(false) }
            kotlinx.browser.window.asDynamic().addEventListener("resize", resize)
            try { kotlinx.coroutines.awaitCancellation() } finally {
                kotlinx.browser.document.body?.style?.setProperty("overflow", oldOverflow)
                kotlinx.browser.window.asDynamic().removeEventListener("resize", resize)
                opener.current?.takeIf { it.isConnected }?.asDynamic()?.focus()
            }
        }
    }
    MobileShellStyles()
    div {
        className = ClassName("portal-shell")
        div {
            className = ClassName("portal-mobile-brand")
            a {
                href = "#/"
                ariaLabel = "Dot Drives home"
                className = ClassName("dot-home-link")
                span { ariaHidden = true; +"••• " }
                +"dot drives"
            }
            div {
                className = ClassName("portal-mobile-account-controls")
                if (customerAccess.visible) button {
                    type = ButtonType.buttonType
                    className = ClassName("portal-mobile-access")
                    ariaLabel = "${customerAccess.compactLabel}. Open access details"
                    ariaExpanded = moreOpen
                    ariaControls = ElementId("portal-more-sheet")
                    onClick = { openMore() }
                    +customerAccess.compactLabel
                }
                button { type = ButtonType.buttonType; ariaLabel = "Open account and navigation"; ariaExpanded = moreOpen; ariaControls = ElementId("portal-more-sheet"); onClick = { openMore() }; +props.userInitials }
            }
        }
        aside {
            className = ClassName("portal-sidebar")
            com.dotdrives.web.pages.admin.CustomerAccessBanner { state = customerAccess }

            a {
                href = "#/"
                ariaLabel = "Dot Drives home"
                className = ClassName("portal-brand dot-home-link")
                span {
                    className = ClassName("portal-brand-mark")
                    ariaHidden = true
                    +"•••"
                }
                span {
                    className = ClassName("portal-brand-name")
                    +"dot drives"
                }
            }

            if (organizationSelection != null) div {
                className = ClassName("portal-organization-desktop")
                OrganizationSelector()
            }

            nav {
                className = ClassName("portal-nav")
                ariaLabel = "Portal navigation"
                NavItem.entries.forEach { item ->
                    button {
                        type = ButtonType.buttonType
                        ariaLabel = item.label
                        title = item.label
                        className = ClassName(
                            "portal-nav-item" + if (item == props.active) " portal-nav-item-active" else "",
                        )
                        if (item == props.active) ariaCurrent = "page".unsafeCast<AriaCurrent>()
                        onClick = { props.onNavigate(item) }
                        span {
                            className = ClassName("portal-nav-icon")
                            ariaHidden = true
                            NavIcon { this.item = item }
                        }
                        span {
                            +item.label
                        }
                    }
                }
            }

            div {
                className = ClassName("portal-user")
                DotAvatar {
                    initials = props.userInitials
                    size = 34
                    className = "portal-user-avatar"
                    decorative = true
                }
                div {
                    className = ClassName("portal-user-copy")
                    div {
                        className = ClassName("portal-user-name")
                        +props.userName
                    }
                    div {
                        className = ClassName("portal-user-org")
                        +props.orgName
                    }
                }
                if (canSwitchOrg && organizationSelection == null) button {
                    type = ButtonType.buttonType
                    className = ClassName("portal-sign-out")
                    title = "Switch organization"
                    ariaLabel = "Switch organization"
                    onClick = { props.onSwitchOrg() }
                    +"⇄"
                }
                button {
                    type = ButtonType.buttonType
                    className = ClassName("portal-sign-out")
                    title = "Sign out"
                    ariaLabel = "Sign out"
                    onClick = { props.onSignOut() }
                    +"↪"
                }
            }
        }

        div {
            className = ClassName("portal-workspace")
            if (organizationSelection != null) div {
                className = ClassName("portal-organization-mobile")
                OrganizationSelector()
            }
            header {
                className = ClassName("portal-topbar")
                div {
                    className = ClassName("portal-page-title dot-heading dot-h2")
                    +props.active.label
                    props.titleSuffix?.let { suffix ->
                        span {
                            className = ClassName("givers-count")
                            +suffix
                        }
                    }
                }
                props.actions?.let { actions ->
                    div {
                        className = ClassName("portal-topbar-actions")
                        +actions
                    }
                }
            }
            nav {
                className = ClassName("portal-bottom-nav")
                ariaLabel = "Mobile navigation"
                listOf(NavItem.Dashboard to "Home", NavItem.NextSteps to "Steps", NavItem.Drives to "Drives", NavItem.Givers to "Givers").forEach { (item, label) ->
                    button { type = ButtonType.buttonType; className = ClassName(if (props.active == item) "mobile-active" else ""); onClick = { setMoreOpen(false); props.onNavigate(item) }; if (item == props.active) ariaCurrent = "page".unsafeCast<AriaCurrent>(); span { NavIcon { this.item = item } }; +label }
                }
                button { type = ButtonType.buttonType; ariaExpanded = moreOpen; ariaControls = ElementId("portal-more-sheet"); className = ClassName(if (props.active in secondaryDestinations) "mobile-active" else ""); onClick = { openMore() }; span { +"•••" }; +"More" }
            }
            if (moreOpen) div {
                className = ClassName("portal-more-backdrop")
                onClick = { if (it.target == it.currentTarget) setMoreOpen(false) }
                div {
                    id = ElementId("portal-more-sheet")
                    className = ClassName("portal-mobile-more")
                    role = "dialog".unsafeCast<AriaRole>()
                    ariaModal = true
                    ariaLabel = "More navigation and account"
                    button { type = ButtonType.buttonType; className = ClassName("portal-more-close"); onClick = { setMoreOpen(false) }; +"Close menu" }
                    com.dotdrives.web.pages.admin.CustomerAccessBanner { state = customerAccess }
                    nav {
                        ariaLabel = "More destinations"
                        secondaryDestinations.forEach { item ->
                            button {
                                type = ButtonType.buttonType
                                if (item == props.active) ariaCurrent = "page".unsafeCast<AriaCurrent>()
                                className = ClassName(if (item == props.active) "portal-more-current" else "")
                                onClick = { setMoreOpen(false); props.onNavigate(item) }
                                NavIcon { this.item = item }
                                +item.label
                            }
                        }
                    }
                    div {
                        className = ClassName("portal-more-account")
                        div { +props.userName }
                        div { +props.orgName }
                        if (canSwitchOrg && organizationSelection == null) button { type = ButtonType.buttonType; onClick = { setMoreOpen(false); props.onSwitchOrg() }; +"Switch organization" }
                        else if (organizationSelection?.organizations?.size == 1 || (organizationSelection == null && !canSwitchOrg)) div { className = ClassName("portal-single-org"); +"This is your only organization." }
                        button { type = ButtonType.buttonType; onClick = { props.onSignOut() }; +"Sign out" }
                    }
                }
            }
            main {
                className = ClassName("portal-content")
                +props.children
            }
        }
    }
}

internal val secondaryDestinations = listOf(NavItem.Donations, NavItem.Forms, NavItem.Updates, NavItem.Reports, NavItem.Settings)
